import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EmployeeUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  access_role: string;
  permissions: {
    custom?: string[];
  } | null;
  avatar_url: string | null;
  shop_id: string;
  email: string;
}

interface EmployeeAuthContextType {
  employee: EmployeeUser | null;
  shopId: string | null;
  sessionToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (shopId: string, employeeId: string, pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  refreshSession: () => Promise<void>;
}

const EmployeeAuthContext = createContext<EmployeeAuthContextType | undefined>(undefined);

const EMPLOYEE_SESSION_KEY = "employee_session_token";

// Role-based permission presets
const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: [
    "view_dashboard", "view_bookings", "manage_bookings", "view_customers", "manage_customers",
    "view_invoices", "create_invoices", "process_payments", "view_reports",
    "manage_inventory", "order_parts", "view_employees", "manage_employees", 
    "view_payroll", "process_payroll", "manage_settings"
  ],
  manager: [
    "view_dashboard", "view_bookings", "manage_bookings", "view_customers", "manage_customers",
    "view_invoices", "create_invoices", "process_payments", "view_reports",
    "manage_inventory", "order_parts", "view_employees", "manage_employees", "view_payroll"
  ],
  hr: [
    "view_dashboard", "view_employees", "manage_employees", "view_payroll", "process_payroll",
    "view_bookings", "view_customers"
  ],
  service_writer: [
    "view_dashboard", "view_bookings", "manage_bookings", "view_customers", "manage_customers",
    "view_invoices", "create_invoices", "view_reports"
  ],
  lead_technician: [
    "view_dashboard", "view_bookings", "view_customers", "view_invoices", "manage_inventory"
  ],
  technician: [
    "view_dashboard", "view_bookings", "view_customers"
  ],
  parts_clerk: [
    "view_dashboard", "manage_inventory", "order_parts"
  ],
  cashier: [
    "view_dashboard", "view_invoices", "process_payments"
  ],
};

export const EmployeeAuthProvider = ({ children }: { children: ReactNode }) => {
  const [employee, setEmployee] = useState<EmployeeUser | null>(null);
  const [shopId, setShopId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getEffectivePermissions = useCallback((emp: EmployeeUser): string[] => {
    // If custom permissions are set, use those
    if (emp.permissions?.custom && emp.permissions.custom.length > 0) {
      return emp.permissions.custom;
    }
    // Otherwise, use role-based permissions
    return ROLE_PERMISSIONS[emp.access_role] || ROLE_PERMISSIONS[emp.role] || [];
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!employee) return false;
    
    // Admin and manager have all permissions
    if (employee.access_role === "admin" || employee.access_role === "manager") {
      return true;
    }
    
    const permissions = getEffectivePermissions(employee);
    return permissions.includes(permission);
  }, [employee, getEffectivePermissions]);

  const validateSession = useCallback(async (token: string) => {
    try {
      const { data, error } = await supabase.rpc("validate_employee_session", {
        _session_token: token,
      });

      const result = data as { valid?: boolean; employee?: EmployeeUser; shop_id?: string } | null;
      if (error || !result?.valid) {
        return null;
      }

      return {
        employee: result.employee as EmployeeUser,
        shopId: result.shop_id as string,
      };
    } catch (err) {
      console.error("Session validation error:", err);
      return null;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const token = sessionToken || localStorage.getItem(EMPLOYEE_SESSION_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }

    const result = await validateSession(token);
    if (result) {
      setEmployee(result.employee);
      setShopId(result.shopId);
      setSessionToken(token);
    } else {
      // Invalid session, clear it
      localStorage.removeItem(EMPLOYEE_SESSION_KEY);
      setEmployee(null);
      setShopId(null);
      setSessionToken(null);
    }
    setIsLoading(false);
  }, [sessionToken, validateSession]);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem(EMPLOYEE_SESSION_KEY);
      if (token) {
        await refreshSession();
      } else {
        setIsLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = async (
    targetShopId: string,
    employeeId: string,
    pin: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc("employee_pin_login", {
        _shop_id: targetShopId,
        _employee_id: employeeId,
        _pin: pin,
      });

      const result = data as { success?: boolean; error?: string; session_token?: string; employee?: EmployeeUser } | null;

      if (error) {
        return { success: false, error: error.message };
      }

      if (!result?.success) {
        return { success: false, error: result?.error || "Login failed" };
      }

      // Store session
      localStorage.setItem(EMPLOYEE_SESSION_KEY, result.session_token!);
      setSessionToken(result.session_token!);
      setEmployee(result.employee as EmployeeUser);
      setShopId(targetShopId);

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    if (sessionToken) {
      await supabase.rpc("employee_logout", {
        _session_token: sessionToken,
      });
    }
    
    localStorage.removeItem(EMPLOYEE_SESSION_KEY);
    setEmployee(null);
    setShopId(null);
    setSessionToken(null);
  };

  return (
    <EmployeeAuthContext.Provider
      value={{
        employee,
        shopId,
        sessionToken,
        isLoading,
        isAuthenticated: !!employee,
        login,
        logout,
        hasPermission,
        refreshSession,
      }}
    >
      {children}
    </EmployeeAuthContext.Provider>
  );
};

export const useEmployeeAuth = () => {
  const context = useContext(EmployeeAuthContext);
  if (context === undefined) {
    throw new Error("useEmployeeAuth must be used within an EmployeeAuthProvider");
  }
  return context;
};

// Custom hook to check permissions
export const useEmployeePermission = (permission: string): boolean => {
  const { hasPermission } = useEmployeeAuth();
  return hasPermission(permission);
};

// Higher-order component for permission-based rendering
export const withEmployeePermission = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission: string
) => {
  return function WithPermissionComponent(props: P) {
    const hasPermission = useEmployeePermission(requiredPermission);
    
    if (!hasPermission) {
      return null;
    }
    
    return <WrappedComponent {...props} />;
  };
};
